import { Trans } from 'react-i18next';

import { Button, Modal } from 'antd';
import { useCallback } from 'react';
import type FileAccess from 'interfaces/FileAccess';
import type { FileAccessNotificationSetting } from 'interfaces/FileAccessNotificationSetting';
import type {
  DeleteFileAccessPayload,
  RevokeDenyFileAccessPayload,
  UpdateFilesAccessPayload,
} from 'interfaces/FileApiService';
import type Group from 'interfaces/Group';
import type User from 'interfaces/User';
import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import { FileLimitationEnum } from '../../../enums/FileLimitationEnum';
import { getApiErrorField, isApiError } from '../../../utils/apiErrorCode';
import { getAntivirusInfectedMessage } from '../../../utils/antivirus';
import type File from '../../../interfaces/File';
import { FileShareForm } from '../../../components/Forms/FileShareForm';
import { FilePublicLinkForm } from '../../../components/Forms/FilePublicLinkForm';
import { hasFileLimitation } from '../../../utils/utils';
import { useOpenGuardedFormModal } from '../../useOpenGuardedFormModal';
import type { UseFilesActionsDeps } from '../deps';
import { fileService } from '../services';
import type { UseFilesActionsEvents, UseFilesActionsReturn } from '../types';
import { isSharedAfterAccessRevoke } from './isSharedAfterAccessRevoke';

/**
 * Sharing and public-link handlers: builds access payloads, applies notification overrides, and drives modals.
 */
export function useFilesShareActions(
  deps: UseFilesActionsDeps,
): Pick<UseFilesActionsReturn, 'handleShareFiles' | 'handleCreatePublicLink'> {
  const { modal, notification, message, t, setIsModalLoading, isModalLoading } = deps;
  const openGuardedFormModal = useOpenGuardedFormModal();

  const shareFiles = useCallback(
    (
      files: File[],
      newUsers: User[],
      newGroups: Group[],
      fileAccessesToRemove: FileAccess[],
      fileAccessesToUpdate: FileAccess[],
      fileAccessesRevokeDenied: FileAccess[],
      sendMessage: boolean,
      shareMessage: string,
      readOnly: boolean,
      expiry: number,
      closeModalAfterSubmit: boolean,
      notificationSettings: FileAccessNotificationSetting | null,
      recipientNotificationOverrides: Record<string, FileAccessNotificationSetting | null>,
      currentAccesses: FileAccess[],
      events?: UseFilesActionsEvents,
    ) => {
      let payload: UpdateFilesAccessPayload = {
        allRecipients: [],
        files: [],
      };
      if (newUsers.length === 0 && newGroups.length === 0 && fileAccessesToUpdate.length > 0) {
        payload = {
          allRecipients: [],
          files: files.map((f) => f.cufi),
        };

        fileAccessesToUpdate.forEach((fileAccess) => {
          const isUser = !!fileAccess.user;

          payload.allRecipients.push({
            recipient: isUser ? fileAccess.user?.email || '' : fileAccess.group?.name || '',
            isGroup: !isUser,
            limitations: {
              expiry: fileAccess.fileLimitation.accessExpiration,
              readOnly: !fileAccess.fileLimitation.canEdit,
            },
            recipientId: fileAccess.user?.id || fileAccess.group?.id || undefined,
          });
        });
      } else if (newUsers.length > 0 || newGroups.length > 0) {
        // Create and send a request for sharing files with these recipients, applying specified limitations.
        payload = {
          // Create an array of object with users and groups
          allRecipients: [
            ...newUsers.map((u) => ({
              recipient: u.email,
              isGroup: false,
            })),
            ...newGroups.map((g) => ({
              recipient: g.name,
              isGroup: true,
            })),
          ],
          files: files.map((f) => f.cufi),
          limitations: {
            expiry: expiry,
            readOnly: readOnly,
          },
        };
        // Add message if showMessageBox is true and messageForNewShare is not empty
        if (sendMessage && shareMessage.trim() !== '') {
          payload.message = shareMessage;
        } else if (sendMessage) {
          payload.message = '';
        } else {
          payload.message = null;
        }
      } else if (
        newUsers.length === 0 &&
        newGroups.length === 0 &&
        fileAccessesToUpdate.length === 0 &&
        // Share-again (revokeDenied) / remove-only must not re-PUT every access:
        // stale folder-inherited expiry can exceed mandatory max → MANDATORY_EXPIRATION_EXCEEDED
        // even after revokedenied already reactivated the share.
        fileAccessesRevokeDenied.length === 0 &&
        fileAccessesToRemove.length === 0 &&
        currentAccesses.length > 0
      ) {
        // No permission changes, but we still need to send notification settings.
        // Build the payload with all current accesses so the API can apply the settings.
        payload = {
          allRecipients: currentAccesses.map((fa) => {
            const isUser = !!fa.user;
            return {
              recipient: isUser ? fa.user?.email || '' : fa.group?.name || '',
              isGroup: !isUser,
              limitations: {
                expiry: fa.fileLimitation.accessExpiration,
                readOnly: !fa.fileLimitation.canEdit,
              },
              recipientId: fa.user?.id || fa.group?.id || undefined,
            };
          }),
          files: files.map((f) => f.cufi),
        };
      }

      // Global notification settings only when adding new users/groups (new share).
      // When only editing existing accesses, each recipient gets its own notificationSettings from overrides.
      const hasNewRecipients = newUsers.length > 0 || newGroups.length > 0;
      if (hasNewRecipients && notificationSettings != null && notificationSettings.enabled) {
        payload.notificationSettings = { ...notificationSettings };
      }

      // Per-recipient notification: look up by id when available (existing accesses), else by recipient string (new)
      const getOverrideKey = (r: (typeof payload.allRecipients)[0]) => {
        if (r.recipientId != null) {
          return r.isGroup ? `group-${r.recipientId}` : `user-${r.recipientId}`;
        }
        return r.isGroup ? `group-${r.recipient}` : `user-${r.recipient}`;
      };
      payload.allRecipients.forEach((r) => {
        const key = getOverrideKey(r);
        const override = recipientNotificationOverrides[key];
        if (override != null) {
          r.notificationSettings = override;
        } else if (
          hasNewRecipients &&
          notificationSettings != null &&
          notificationSettings.enabled
        ) {
          // New recipients use global default when no override
          r.notificationSettings = notificationSettings;
        }
      });

      setIsModalLoading(true);

      // One success toast per save: update wins over revoke when both run.
      const hasUpdateRequest = payload.allRecipients.length > 0;
      const hasRemoveRequest = fileAccessesToRemove.length > 0;

      // If there are file accesses to revoke denied, revoke them
      if (fileAccessesRevokeDenied.length > 0) {
        const revokeDeniedPayload: RevokeDenyFileAccessPayload = {
          fileIds: files.map((f) => f.id),
          recipients: fileAccessesRevokeDenied.map((f) =>
            !!f.user
              ? {
                  id: f.user.id || 0,
                  isGroup: false,
                }
              : {
                  id: f.group?.id || 0,
                  isGroup: true,
                },
          ),
        };

        fileService
          .revokeDeniedFileAccess(revokeDeniedPayload)
          .then(() => {
            // Skip toast when update or remove also succeeds (those own the message).
            if (!hasUpdateRequest && !hasRemoveRequest) {
              message.success(t('notifications.fileAccessRevoked'));
            }
            if (closeModalAfterSubmit) {
              Modal.destroyAll();
            }
          })
          .catch((error) => {
            // Handle specific errors
            if (isApiError(error.response, ApiErrorEnum.EditSharingDenied)) {
              notification.error({
                title: t('errors.editSharingDenied'),
              });
            } else {
              notification.error({
                title: t('errors.shareFiles'),
              });
            }
          })
          .finally(() => {
            setIsModalLoading(false);
          });
      }

      // Update the file accesses if there are any
      if (payload.allRecipients.length > 0) {
        setIsModalLoading(true);
        fileService
          .updateFilesAccess(payload)
          .then(() => {
            // Update each file by setting the isShared property to true
            events?.onFilesUpdated?.(
              files.map((f) => ({
                ...f,
                isShared: true,
              })),
            );
            if (closeModalAfterSubmit) {
              Modal.destroyAll();
            }
            message.success(t('notifications.fileAccessUpdated'));
          })
          .catch((error) => {
            setIsModalLoading(false);
            if (isApiError(error.response, ApiErrorEnum.AntivirusFileInfected)) {
              const fileIdRaw =
                getApiErrorField(error.response, 'fileId') ??
                getApiErrorField(error.response, 'file_id');
              const threatName =
                getApiErrorField(error.response, 'threatName') ??
                getApiErrorField(error.response, 'threat_name');
              const offendingFile =
                fileIdRaw != null
                  ? files.find((candidate) => String(candidate.id) === fileIdRaw)
                  : files[0];
              notification.error({
                title: getAntivirusInfectedMessage(t, {
                  threatName,
                  fileName: offendingFile?.fileName,
                }),
              });
            } else if (isApiError(error.response, ApiErrorEnum.EditSharingDenied)) {
              notification.error({
                title: t('errors.editSharingDenied'),
              });
            } else if (isApiError(error.response, ApiErrorEnum.PartialSharingDenied)) {
              notification.error({
                title: t('errors.partialSharingDeniedDisabledRecipients', {
                  shareAgainLabel: t('common.shareAgain'),
                }),
              });
            } else if (error.response && typeof error.response === 'object') {
              try {
                const invalidResponse =
                  typeof error.response === 'string'
                    ? JSON.parse(error.response)
                    : (error.response as { unauthorizedEmails?: string[] });
                const invalidEmails = invalidResponse.unauthorizedEmails;
                const currentEmails = payload.allRecipients
                  .filter((recipient) => recipient.isGroup === false)
                  .map((recipient) => recipient.recipient);
                if (
                  invalidEmails &&
                  Array.isArray(invalidEmails) &&
                  invalidEmails.length === currentEmails.length
                ) {
                  const modalInstance = modal.confirm({
                    title: t('modal.fileShareFormUnauthorizedUsers.title'),
                    content: (
                      <div className="flex flex-col gap-2">
                        <p>{t('modal.fileShareFormUnauthorizedUsers.descriptionForAllUsers')}</p>
                        <ul className="text-blue-dark list-disc pl-5 text-sm font-medium">
                          {invalidEmails.map((email) => (
                            <li key={email}>{email}</li>
                          ))}
                        </ul>
                        <div
                          className="mt-4 flex flex-row items-center justify-end"
                          role="group"
                          aria-label={t('modal.fileShareForm.actions')}
                        >
                          <div className="flex flex-row items-center justify-center gap-2">
                            <Button
                              size="middle"
                              onClick={() => modalInstance.destroy()}
                              aria-label={t('common.cancel')}
                            >
                              {t('common.cancel')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ),
                    footer: null,
                  });
                } else if (
                  invalidEmails &&
                  Array.isArray(invalidEmails) &&
                  invalidEmails.length > 0
                ) {
                  const modalInstance = modal.confirm({
                    title: t('modal.fileShareFormUnauthorizedUsers.title'),
                    content: t('modal.fileShareFormUnauthorizedUsers.descriptionForSomeUsers', {
                      users: invalidEmails.join(', '),
                    }),
                    okText: t('common.confirm'),
                    cancelText: t('common.cancel'),
                    onOk: () => {
                      setIsModalLoading(true);
                      // Remove all invalid mail for the new payload
                      payload.allRecipients = payload.allRecipients.filter(
                        (recipient) => !invalidEmails.includes(recipient.recipient),
                      );
                      // Make the api call again
                      fileService
                        .updateFilesAccess(payload)
                        .then(() => {
                          message.success(t('notifications.fileAccessUpdated'));
                          modalInstance.destroy();
                        })
                        .catch((retryError) => {
                          // Handle specific errors in retry
                          if (isApiError(retryError.response, ApiErrorEnum.EditSharingDenied)) {
                            notification.error({
                              title: t('errors.editSharingDenied'),
                            });
                          } else if (
                            isApiError(retryError.response, ApiErrorEnum.PartialSharingDenied)
                          ) {
                            notification.error({
                              title: t('errors.partialSharingDeniedDisabledRecipients', {
                                shareAgainLabel: t('common.shareAgain'),
                              }),
                            });
                          } else {
                            notification.error({
                              title: t('errors.shareFiles'),
                            });
                          }
                        })
                        .finally(() => {
                          setIsModalLoading(false);
                        });
                    },
                    onCancel: () => {
                      modalInstance.destroy();
                    },
                  });
                } else {
                  // If structure is unexpected, show generic error
                  setIsModalLoading(false);
                  notification.error({
                    title: t('errors.shareFiles'),
                  });
                }
              } catch {
                // If JSON parsing fails, show generic error
                setIsModalLoading(false);
                notification.error({
                  title: t('errors.shareFiles'),
                });
              }
            } else {
              setIsModalLoading(false);
              notification.error({
                title: t('errors.shareFiles'),
              });
            }
          })
          .finally(() => {
            setIsModalLoading(false);
          });
      }

      if (fileAccessesToRemove.length > 0) {
        // Build the payload for the request
        const deleteFileAccessPayload: DeleteFileAccessPayload = {
          fileIds: files.map((f) => f.id),
          recipients: fileAccessesToRemove.map((f) =>
            !!f.user
              ? {
                  id: f.user.id || 0,
                  isGroup: false,
                }
              : {
                  id: f.group?.id || 0,
                  isGroup: true,
                },
          ),
        };

        setIsModalLoading(true);

        fileService
          .deleteFileAccess(deleteFileAccessPayload)
          .then(() => {
            // currentAccesses = remaining grants (form already dropped revoked ones).
            // DELETE /file/access/v2 has no remaining-count body — derive isShared locally.
            const stillShared = isSharedAfterAccessRevoke(
              currentAccesses.length,
              newUsers.length,
              newGroups.length,
            );
            events?.onFilesUpdated?.(
              files.map((f) => ({
                ...f,
                isShared: stillShared,
              })),
            );
            // Mixed save (update + revoke): only the updated toast fires.
            if (!hasUpdateRequest) {
              message.success(t('notifications.fileAccessRevoked'));
            }
            if (closeModalAfterSubmit) {
              Modal.destroyAll();
            }
          })
          .catch((error) => {
            // Handle specific errors
            if (isApiError(error.response, ApiErrorEnum.EditSharingDenied)) {
              notification.error({
                title: t('errors.editSharingDenied'),
              });
            } else {
              notification.error({
                title: t('errors.errorRemoveShareFiles'),
              });
            }
          })
          .finally(() => {
            setIsModalLoading(false);
          });
      }
    },
    [modal, t, message, notification, setIsModalLoading],
  );

  const handleShareFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      if (
        files.some((f) =>
          hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenySensitiveOperationSharing),
        )
      ) {
        modal.warning({
          title: t('modal.tagsRequiredForShare.title'),
          content: (
            <Trans>
              {t('modal.tagsRequiredForShare.description', {
                resource: files?.[0]?.fileName,
                count: files.length,
              })}
            </Trans>
          ),
        });
        return;
      }

      const modalInstance = openGuardedFormModal({
        modalProps: {
          width: 700,
        },
        title: t('modal.fileShareForm.title', {
          resource: files?.[0]?.fileName,
          count: files.length,
        }),
        content: (
          <>
            <Trans>
              <p className="pb-6">
                {t('modal.fileShareForm.message', {
                  resource: files?.[0]?.fileName,
                  count: files.length,
                })}
              </p>
            </Trans>
            <FileShareForm
              selectedFiles={files}
              onSubmit={(
                newUsers,
                newGroups,
                fileAccessesToRemove,
                fileAccessesToUpdate,
                fileAccessesRevokeDenied,
                sendMessage,
                shareMsg,
                readOnly,
                expiry,
                closeModalAfterSubmit,
                notificationSettings,
                recipientNotificationOverrides,
                currentAccessesForPayload,
              ) => {
                shareFiles(
                  files,
                  newUsers,
                  newGroups,
                  fileAccessesToRemove,
                  fileAccessesToUpdate,
                  fileAccessesRevokeDenied,
                  sendMessage,
                  shareMsg,
                  readOnly,
                  expiry,
                  closeModalAfterSubmit,
                  notificationSettings,
                  recipientNotificationOverrides,
                  currentAccessesForPayload,
                  events,
                );
              }}
              onCancel={() => {
                modalInstance.destroy();
              }}
            />
          </>
        ),
      });
    },
    [openGuardedFormModal, t, shareFiles],
  );

  const handleCreatePublicLink = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const modalInstance = modal.confirm({
        title: t('modal.filePublicLinkForm.title'),
        content: (
          <FilePublicLinkForm
            file={file}
            onCancel={() => modalInstance.destroy()}
            onCreated={() => {
              events?.onFilesUpdated?.([{ ...file, isPublicShare: true }]);
            }}
            onRevoked={() => {
              events?.onFilesUpdated?.([{ ...file, isPublicShare: false }]);
            }}
            isLoading={isModalLoading}
          />
        ),
        footer: null,
      });
    },
    [modal, t, isModalLoading],
  );

  return {
    handleShareFiles,
    handleCreatePublicLink,
  };
}
